import { ExcludedServers, OutputEc2, OutputRDS } from '@amzn/awscat-portfolio-assessment-types';

import { constants, DatabaseWarningMessages, ServerWarningMessages } from '../../../common/Localization';
import { generateDedicatedHostRemappingButton } from '../aws/ec2-recommendations/DedicatedHostToSharedButton';
import { ErrorMsgFormat } from './CommonTcoErrorHandler';
export const generateEc2ErrorMsgs = (ec2Output: OutputEc2, portfolioId: string): ErrorMsgFormat[] => {
    const errorMsgs: ErrorMsgFormat[] = [];

    // Generate out of scope message
    if (ec2Output.excludedServers?.outOfScope && ec2Output.excludedServers?.outOfScope > 0) {
        errorMsgs.push({
            count: ec2Output.excludedServers.outOfScope,
            targetColumn: 'inScope',
            targetValue: ['false'],
            path: `/portfolio/${portfolioId}/management/assets/servers`,
            singular: constants.SERVER_LOWERCASE,
            text: ServerWarningMessages.OUT_OF_SCOPE,
        });
    }

    // Generate unmatched EC2 servers message
    if (ec2Output.unmatchedServerIds?.length > 0) {
        errorMsgs.push({
            count: ec2Output.unmatchedServerIds?.length,
            targetColumn: 'serverId',
            targetValue: ec2Output.unmatchedServerIds,
            path: `/portfolio/${portfolioId}/management/assets/servers`,
            singular: constants.SERVER_LOWERCASE,
            text: ServerWarningMessages.UNMATCHED_SERVER,
        });
    }

    // Generate failed Dedicated Host servers message
    if (ec2Output.failedDedicatedHostServers && ec2Output.failedDedicatedHostServers.length > 0) {
        errorMsgs.push({
            count: ec2Output.failedDedicatedHostServers.length,
            targetColumn: 'serverId',
            targetValue: ec2Output.failedDedicatedHostServers,
            path: `/portfolio/${portfolioId}/management/assets/servers`,
            singular: constants.SERVER_LOWERCASE,
            text: ServerWarningMessages.FAILED_DEDICATED_HOST,
        });
    }

    // Generate mapping Dedicated Host servers to Shared tenancy message
    if (ec2Output.dedicatedHostServersToShared && ec2Output.dedicatedHostServersToShared.length > 0) {
        errorMsgs.push({
            count: ec2Output.dedicatedHostServersToShared.length,
            targetColumn: 'serverId',
            targetValue: ec2Output.dedicatedHostServersToShared,
            path: `/portfolio/${portfolioId}/management/assets/servers`,
            singular: constants.SERVER_LOWERCASE,
            text: ServerWarningMessages.MAPPED_TO_SHARED_DEDICATED_HOST,
            tailingComponent: generateDedicatedHostRemappingButton(portfolioId, ec2Output.dedicatedHostServersToShared),
        });
    } 

    // Generate database servers message
    if (ec2Output.numOfDatabaseServers > 0) {
        errorMsgs.push({
            count: ec2Output.numOfDatabaseServers,
            singular: constants.SERVER_LOWERCASE,
            targetColumn: 'databaseMapped',
            targetValue: [ constants.RESOURCE_MAPPED ],
            path: `/portfolio/${portfolioId}/management/assets/servers`,
            text: ServerWarningMessages.DATABASE_SERVER,
        });
    }

    // Generate retired servers message
    if (ec2Output.excludedServers?.Retire && ec2Output.excludedServers?.Retire > 0) {
        errorMsgs.push({
            count: ec2Output.excludedServers.Retire,
            singular: constants.SERVER_LOWERCASE,
            text: ServerWarningMessages.RETIRED_SERVER,
        });
    }

    // Generate retained servers message
    if (ec2Output.excludedServers?.Retain && ec2Output.excludedServers?.Retain > 0) {
        errorMsgs.push({
            count: ec2Output.excludedServers.Retain,
            singular: constants.SERVER_LOWERCASE,
            text: ServerWarningMessages.RETAINED_SERVER,
        });
    }

    return errorMsgs;
};

export const generateRdsErrorMsgs = (rdsOutput: OutputRDS, ec2Output: OutputEc2, portfolioId: string, tcoAssessmentId: string | null): ErrorMsgFormat[] => {
    const errorMsgs: ErrorMsgFormat[] = [];

    // Generate unmatched dbs message
    if (rdsOutput.unmatchedDatabaseIds?.length > 0) {
        errorMsgs.push({
            count: rdsOutput.unmatchedDatabaseIds.length,
            targetColumn: 'databaseId',
            targetValue: rdsOutput.unmatchedDatabaseIds,
            path: `/portfolio/${portfolioId}/management/assets/databases`,
            singular: constants.DATABASE_LOWERCASE,
            text: DatabaseWarningMessages.UNMATCHED_DATABASE,
        });
    }

    // Added optional chaining here for backwards compatibility with older RDS recommendations
    if (rdsOutput.unlinkedDatabaseIds?.length > 0) {
        errorMsgs.push({
            count: rdsOutput.unlinkedDatabaseIds.length,
            targetColumn: 'databaseId',
            targetValue: rdsOutput.unlinkedDatabaseIds,
            path: `/portfolio/${portfolioId}/management/assets/databases`,
            singular: constants.DATABASE_LOWERCASE,
            plural: constants.DATABASES_LOWERCASE,
            text: DatabaseWarningMessages.UNLINKED_DATABASE
        });
    }

    // Generate EC2 with sql server database message
    if (rdsOutput.numOfEC2DatabaseServers > 0) {
        errorMsgs.push({
            count: rdsOutput.numOfEC2DatabaseServers,
            singular: constants.DATABASE_LOWERCASE,
            plural: constants.DATABASES_LOWERCASE,
            targetColumn: 'preInstalledSw',
            targetValue: [constants.SQL_SERVER_ENT, constants.SQL_SERVER_STD, constants.SQL_SERVER_WEB],
            targetTab: 'paymentOptions',
            path: `/portfolio/${portfolioId}/tco/${tcoAssessmentId}/aws/server-costs`,
            text: DatabaseWarningMessages.EC2_SQL_SERVER_DATABASE,
        });
    }

    if (rdsOutput.ec2Recommendations && (rdsOutput.ec2Recommendations.oracle.databaseIds.length > 0)) {
        errorMsgs.push({
            count: rdsOutput.ec2Recommendations.oracle.databaseIds.length,
            targetColumn: 'databaseId',
            targetValue: rdsOutput.ec2Recommendations?.oracle.databaseIds,
            path: `/portfolio/${portfolioId}/management/assets/databases`,
            singular: constants.DATABASE_LOWERCASE,
            plural: constants.DATABASES_LOWERCASE,
            text: DatabaseWarningMessages.EC2_ORACLE_DATABASE
        });
    }

    if(ec2Output.sharedTenancyOsCostResults?.oracleCost.databaseIdsOverStandardLicenseLimit && 
        (ec2Output.sharedTenancyOsCostResults.oracleCost.databaseIdsOverStandardLicenseLimit.length > 0)) {
        errorMsgs.push({
            count: ec2Output.sharedTenancyOsCostResults.oracleCost.databaseIdsOverStandardLicenseLimit.length,
            targetColumn: 'databaseId',
            targetValue: ec2Output.sharedTenancyOsCostResults.oracleCost.databaseIdsOverStandardLicenseLimit,
            path: `/portfolio/${portfolioId}/management/assets/databases`,
            singular: constants.ORACLE_STANDARD_DATABASE,
            plural: constants.ORACLE_STANDARD_DATABASES,
            text: DatabaseWarningMessages.ORACLE_STANDARD_LICENSE_OVER_LIMIT
        });
    }

    if(ec2Output.sharedTenancyOsCostResults?.oracleCost.databaseIdsOverStandardOneOrTwoLicenseLimit && 
        (ec2Output.sharedTenancyOsCostResults.oracleCost.databaseIdsOverStandardOneOrTwoLicenseLimit.length > 0)) {
        errorMsgs.push({
            count: ec2Output.sharedTenancyOsCostResults.oracleCost.databaseIdsOverStandardOneOrTwoLicenseLimit.length,
            targetColumn: 'databaseId',
            targetValue: ec2Output.sharedTenancyOsCostResults?.oracleCost.databaseIdsOverStandardOneOrTwoLicenseLimit,
            path: `/portfolio/${portfolioId}/management/assets/databases`,
            singular: constants.ORACLE_STANDARD_ONE_TWO_DATABASE,
            plural: constants.ORACLE_STANDARD_ONE_TWO_DATABASES,
            text: DatabaseWarningMessages.ORACLE_STANDARD_ONE_TWO_LICENSE_OVER_LIMIT
        });
    } 
    
    return errorMsgs;
};

export const generateExcludedServersErrorMsgs = (excludedServers: ExcludedServers, portfolioId: string): ErrorMsgFormat[] => {
    const errorMsgs: ErrorMsgFormat[] = [];

    if (excludedServers.outOfScope > 0) {
        errorMsgs.push({
            count: excludedServers.outOfScope,
            targetColumn: 'inScope',
            targetValue: ['false'],
            path: `/portfolio/${portfolioId}/management/assets/servers`,
            singular: constants.SERVER_LOWERCASE,
            text: ServerWarningMessages.OUT_OF_SCOPE,
        });
    }

    // Generate retired servers message
    if (excludedServers.Retire && excludedServers.Retire > 0) {
        errorMsgs.push({
            count: excludedServers.Retire,
            singular: constants.SERVER_LOWERCASE,
            text: ServerWarningMessages.RETIRED_SERVER,
        });
    }

    // Generate retained servers message
    if (excludedServers.Retain && excludedServers.Retain > 0) {
        errorMsgs.push({
            count: excludedServers.Retain,
            singular: constants.SERVER_LOWERCASE,
            text: ServerWarningMessages.RETAINED_SERVER,
        });
    }

    return errorMsgs;
};
